import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Login() {
  const [loginMethod, setLoginMethod] = useState('username'); // 'username' or 'crm'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // CRM Location Login states
  const [locationId, setLocationId] = useState('');
  const [crmStep, setCrmStep] = useState(1); // 1: location ID, 2: security question, 3: temp password sent
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [crmUsername, setCrmUsername] = useState('');
  const [crmEmailHint, setCrmEmailHint] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  
  const [loading, setLoading] = useState(false);
  const { login, setToken, setUser } = useAuth();
  const navigate = useNavigate();

  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(username, password);

    if (result.success) {
      // Show CRM auto-connect message if applicable
      if (result.ghl_auto_connected) {
        toast.success('CRM connected automatically!', {
          description: `Location: ${result.ghl_location_id}`,
          duration: 5000
        });
      }
      
      if (result.force_password_change) {
        toast.warning('Please change your temporary password');
        navigate('/settings');
      } else {
        toast.success('Login successful!');
        navigate('/');
      }
    } else {
      toast.error(result.error || 'Login failed');
    }

    setLoading(false);
  };

  const handleCrmVerifyLocation = async (e) => {
    e.preventDefault();
    if (!locationId.trim()) {
      toast.error('Please enter your CRM Location ID');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/ghl-login/verify-location`, {
        location_id: locationId.trim()
      });
      
      setCrmUsername(response.data.username);
      setSecurityQuestion(response.data.security_question);
      setCrmStep(2);
      toast.success('Location ID verified');
    } catch (error) {
      console.error('Error verifying location:', error);
      toast.error(error.response?.data?.detail || 'Location ID not found');
    } finally {
      setLoading(false);
    }
  };

  const handleCrmSecurityAnswer = async (e) => {
    e.preventDefault();
    if (!securityAnswer.trim()) {
      toast.error('Please provide an answer');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/ghl-login`, {
        location_id: locationId,
        security_answer: securityAnswer
      });
      
      // Security answer verified - temp password was sent
      if (response.data.temp_password_sent) {
        setCrmEmailHint(response.data.email_hint || '');
        setCrmStep(3);
        toast.success('Temporary password sent! Check your email.');
        
        if (response.data.warning) {
          toast.warning(response.data.warning);
        }
      }
    } catch (error) {
      console.error('Error verifying security answer:', error);
      toast.error(error.response?.data?.detail || 'Security answer is incorrect');
    } finally {
      setLoading(false);
    }
  };

  const handleCrmTempPasswordLogin = async (e) => {
    e.preventDefault();
    if (!tempPassword.trim()) {
      toast.error('Please enter the temporary password from your email');
      return;
    }

    setLoading(true);
    try {
      // Use the standard login with the temp password
      const result = await login(crmUsername, tempPassword);
      
      if (result.success) {
        if (result.force_password_change) {
          toast.warning('Please set a new password');
          navigate('/settings');
        } else {
          toast.success('Login successful!');
          navigate('/');
        }
      } else {
        toast.error(result.error || 'Login failed. Please check your temporary password.');
      }
    } catch (error) {
      console.error('Error logging in:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLocationId = () => {
    setCrmStep(1);
    setSecurityAnswer('');
    setSecurityQuestion('');
    setTempPassword('');
    setCrmUsername('');
    setCrmEmailHint('');
  };

  const handleBackToSecurityQuestion = () => {
    setCrmStep(2);
    setTempPassword('');
    setCrmEmailHint('');
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 50%, #f0f9ff 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background gradient effects */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '10%',
        width: '400px',
        height: '400px',
        background: 'rgba(59, 130, 246, 0.15)',
        borderRadius: '50%',
        filter: 'blur(100px)'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '10%',
        right: '10%',
        width: '400px',
        height: '400px',
        background: 'rgba(139, 92, 246, 0.15)',
        borderRadius: '50%',
        filter: 'blur(100px)'
      }} />
      
      <div style={{
        width: '100%',
        maxWidth: '440px',
        padding: '40px',
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '24px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            margin: '0 auto 16px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)'
          }}>
            ✨
          </div>
          <h1 style={{ 
            fontFamily: 'Outfit, sans-serif',
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#0f172a',
            marginBottom: '8px' 
          }}>
            Revolutionary Leads
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b' }}>
            Sign in to your account
          </p>
        </div>

        {/* Login Method Toggle */}
        <div style={{ display: 'flex', marginBottom: '24px', borderBottom: '2px solid #e2e8f0' }}>
          <button
            type="button"
            onClick={() => setLoginMethod('username')}
            style={{
              flex: 1,
              padding: '12px',
              background: 'none',
              border: 'none',
              borderBottom: loginMethod === 'username' ? '2px solid #3b82f6' : '2px solid transparent',
              color: loginMethod === 'username' ? '#3b82f6' : '#64748b',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            Username / Email
          </button>
          <button
            type="button"
            onClick={() => setLoginMethod('crm')}
            style={{
              flex: 1,
              padding: '12px',
              background: 'none',
              border: 'none',
              borderBottom: loginMethod === 'crm' ? '2px solid #3b82f6' : '2px solid transparent',
              color: loginMethod === 'crm' ? '#3b82f6' : '#64748b',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            CRM Location
          </button>
        </div>

        {/* Username/Email Login Form */}
        {loginMethod === 'username' && (
          <form onSubmit={handleUsernameSubmit} data-testid="login-form">
            <div className="form-group">
              <label className="form-label" style={{ color: '#334155' }}>Username or Email</label>
              <input
                type="text"
                className="form-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username or email"
                required
                data-testid="username-input"
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#0f172a'
                }}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ color: '#334155' }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  data-testid="password-input"
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#0f172a',
                    paddingRight: '44px'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="toggle-password-visibility"
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', padding: '6px', cursor: 'pointer',
                    color: '#64748b', display: 'flex', alignItems: 'center'
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              style={{ 
                width: '100%', 
                marginTop: '8px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                border: 'none',
                padding: '12px',
                borderRadius: '10px',
                fontWeight: '600',
                color: '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
              data-testid="login-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        )}

        {/* CRM Location Login Form */}
        {loginMethod === 'crm' && (
          <>
            {/* Step 1: Location ID */}
            {crmStep === 1 && (
              <form onSubmit={handleCrmVerifyLocation}>
                <div className="form-group">
                  <label className="form-label" style={{ color: '#334155' }}>CRM Location ID</label>
                  <input
                    type="text"
                    className="form-input"
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    placeholder="Enter your CRM Location ID"
                    required
                    data-testid="crm-location-input"
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      color: '#0f172a'
                    }}
                  />
                  <p className="form-help" style={{ fontSize: '12px', marginTop: '4px', color: '#64748b' }}>
                    This is the Location ID connected to your account
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  style={{ 
                    width: '100%', 
                    marginTop: '8px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    border: 'none',
                    padding: '12px',
                    borderRadius: '10px',
                    fontWeight: '600',
                    color: '#fff',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }}
                  data-testid="crm-verify-btn"
                >
                  {loading ? 'Verifying...' : 'Continue'}
                </Button>
              </form>
            )}

            {/* Step 2: Security Question */}
            {crmStep === 2 && (
              <form onSubmit={handleCrmSecurityAnswer}>
                <div style={{ marginBottom: '16px', padding: '12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
                  <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Account:</p>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>{crmUsername}</p>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ color: '#334155' }}>Security Question</label>
                  <div style={{
                    padding: '12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#0f172a',
                    marginBottom: '12px'
                  }}>
                    {securityQuestion}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ color: '#334155' }}>Your Answer</label>
                  <input
                    type="text"
                    className="form-input"
                    value={securityAnswer}
                    onChange={(e) => setSecurityAnswer(e.target.value)}
                    placeholder="Enter your answer"
                    required
                    autoFocus
                    data-testid="crm-answer-input"
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      color: '#0f172a'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <Button
                    type="submit"
                    disabled={loading}
                    style={{ 
                      flex: 1,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '10px',
                      fontWeight: '600',
                      color: '#fff',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1
                    }}
                    data-testid="crm-security-btn"
                  >
                    {loading ? 'Verifying...' : 'Verify & Send Password'}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleBackToLocationId}
                    style={{
                      background: '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      padding: '12px 20px',
                      borderRadius: '10px',
                      fontWeight: '600',
                      color: '#475569',
                      cursor: 'pointer'
                    }}
                  >
                    Back
                  </Button>
                </div>
              </form>
            )}

            {/* Step 3: Temp Password Sent - Enter to Login */}
            {crmStep === 3 && (
              <form onSubmit={handleCrmTempPasswordLogin}>
                <div style={{ marginBottom: '16px', padding: '16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '20px' }}>✉️</span>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#16a34a' }}>
                      Temporary Password Sent!
                    </p>
                  </div>
                  <p style={{ fontSize: '13px', color: '#64748b' }}>
                    Check your email {crmEmailHint && `(${crmEmailHint})`} for your temporary password.
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ color: '#334155' }}>Temporary Password</label>
                  <input
                    type="text"
                    className="form-input"
                    value={tempPassword}
                    onChange={(e) => setTempPassword(e.target.value)}
                    placeholder="Enter the password from your email"
                    required
                    autoFocus
                    data-testid="crm-temp-password-input"
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      color: '#0f172a'
                    }}
                  />
                  <p className="form-help" style={{ fontSize: '12px', marginTop: '4px', color: '#64748b' }}>
                    Didn't receive it? Check your spam folder or try again.
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <Button
                    type="submit"
                    disabled={loading}
                    style={{ 
                      flex: 1,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '10px',
                      fontWeight: '600',
                      color: '#fff',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1
                    }}
                    data-testid="crm-login-btn"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleBackToSecurityQuestion}
                    style={{
                      background: '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      padding: '12px 20px',
                      borderRadius: '10px',
                      fontWeight: '600',
                      color: '#475569',
                      cursor: 'pointer'
                    }}
                  >
                    Back
                  </Button>
                </div>
              </form>
            )}
          </>
        )}

        <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '14px' }}>
          <Link to="/forgot-password" style={{ color: '#3b82f6', fontWeight: '600', textDecoration: 'none' }}>
            Forgot Password?
          </Link>
        </div>

        <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
          Don't have an account?{' '}
          <Link to="/register" style={{ color: '#3b82f6', fontWeight: '600', textDecoration: 'none' }}>
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
